import { useState } from "react";
import styled from "@emotion/styled";

import {
  gridFilterModelSelector,
  type GridRenderHeaderFilterProps,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { useGridApiContext } from "@mui/x-data-grid-premium";

import { useGridRootProps } from "@mui/x-data-grid-pro/hooks/utils/useGridRootProps";

import {
  type DateRange,
  DateRangeCalendar,
  PickersActionBar,
  PickersShortcuts,
  type PickersShortcutsItem,
} from "@mui/x-date-pickers-pro";
import type { Dayjs } from "dayjs";
import { default as dayjs } from "dayjs";
import { SingleInputDateRangeField } from "src/components/DateRangePicker/SingleInputDateRangeField.tsx";
import {
  FloatingOverlay,
  FloatingPortal,
  shift,
  useFloating,
} from "@floating-ui/react";
import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { useNonConflictingTheme } from "src/lib/emotion/use-non-conflicting-theme.ts";
import { FLOATING_PORTAL_ROOT_ID } from "src/common/constants.ts";
import { Flex } from "src/components/Flex.tsx";
import { usePickersTranslations } from "@mui/x-date-pickers/hooks";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { arrDeleteAt, arrReplaceAt } from "@exonn/utils";
import { useDataGridStore } from "src/components/DataGrid/DataGridStoreProvider.tsx";
import { observer } from "mobx-react-lite";
import { mergeFilterModels } from "src/components/DataGrid/mergeFilterModels.ts";
type Value = [dayjs.Dayjs | null, dayjs.Dayjs | null];
export const DataGridDateRangeHeaderFilterImpl = observer(
  (props: GridRenderHeaderFilterProps) => {
    const { tabIndex, item } = props;
    const theme = useNonConflictingTheme();
    const pickerTranslations = usePickersTranslations();
    const { t } = useTranslation(TranslationNamespaceEnum.DataGridComponent);
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
    const store = useDataGridStore();
    const appliedItem = store.appliedFilterModel.items.find(
      (a) => a.field === item.field,
    );

    const setOpenCalendar = (open: boolean) => {
      if (open) {
        if (item.value != null) {
          setRange(itemToRange(item));
        } else {
          setRange(itemToRange(item));
        }
      } else {
        setRange(null);
      }
    };
    const { floatingStyles, refs } = useFloating({
      placement: "bottom",
      onOpenChange: (open) => {
        setOpenCalendar(open);
      },
      middleware: [
        shift({
          crossAxis: true,
          padding: 10,
        }),
      ],
    });

    const disableColumnHeaderFilter =
      !!rootProps.slotProps?.headerFilterCell?.InputComponentProps?.disabled;

    // const applyFilterChanges = useCallback(
    //   (updatedItem: GridFilterItem) => {
    //     if (!updatedItem.value) {
    //       apiRef.current.deleteFilterItem(updatedItem);
    //     } else {
    //       apiRef.current.upsertFilterItem(updatedItem);
    //     }
    //   },
    //   [apiRef],
    // );

    const handleDatePickerChanger = (range: DateRange<Dayjs>) => {
      setRange(range);
    };

    const clearRange = () => {
      const mergedFilterModel = mergeFilterModels(
        filterModel,
        store.appliedFilterModel,
      );
      store.setAppliedFilterModel({
        ...mergedFilterModel,
        items: arrDeleteAt(
          mergedFilterModel.items,
          (prevItem) => prevItem.field === item.field,
        ),
      });
      apiRef.current.setFilterModel({
        items: [],
      });
    };

    const applyRange = (value: Value) => {
      const [start, end] = value;

      if (start && start.isValid() && end && end.isValid()) {
        const value =
          start.format("YYYY-MM-DD") + "," + end.format("YYYY-MM-DD");
        const nextItem = {
          ...item,
          value,
        };

        const mergedFilterModel = mergeFilterModels(
          filterModel,
          store.appliedFilterModel,
        );

        const nextItems = arrReplaceAt(
          mergedFilterModel.items,
          (prevItem) => prevItem.field === nextItem.field,
          nextItem,
          true,
        );

        const filteredItems = nextItems.filter((item) => !!item.value);
        const nextFilterModel = {
          ...mergedFilterModel,
          items: filteredItems,
        };

        if (rootProps.filterMode === "server") {
          store.setAppliedFilterModel(nextFilterModel);
          apiRef.current.setFilterModel({
            items: [],
          });
        } else {
          apiRef.current.setFilterModel(nextFilterModel);
        }

        // applyFilterChanges(nextItem);
      } else {
        clearRange();
      }
    };
    const [draftRange, setRange] = useState<Value | null>(null);

    const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
      {
        label: t("dateRangeShortcuts.thisWeek"),
        getValue: () => {
          const today = dayjs();
          return [today.startOf("week"), today.endOf("week")];
        },
      },
      {
        label: t("dateRangeShortcuts.lastWeek"),
        getValue: () => {
          const today = dayjs();
          const prevWeek = today.subtract(7, "day");
          return [prevWeek.startOf("week"), prevWeek.endOf("week")];
        },
      },
      {
        label: t("dateRangeShortcuts.lastSevenDays"),
        getValue: () => {
          const today = dayjs();
          return [today.subtract(7, "day"), today];
        },
      },
      {
        label: t("dateRangeShortcuts.currentMonth"),
        getValue: () => {
          const today = dayjs();
          return [today.startOf("month"), today.endOf("month")];
        },
      },
      {
        label: t("dateRangeShortcuts.nextMonth"),
        getValue: () => {
          const today = dayjs();
          const startOfNextMonth = today.endOf("month").add(1, "day");
          return [startOfNextMonth, startOfNextMonth.endOf("month")];
        },
      },
      {
        label: pickerTranslations.clearButtonLabel,
        getValue: () => [null, null],
      },
    ];

    const getDisplayValue = () => {
      if (draftRange != null) {
        return draftRange;
      }
      if (item.value != null) {
        return itemToRange(item);
      }
      if (appliedItem?.value != null) {
        return itemToRange(appliedItem);
      }

      return itemToRange(item);
    };

    const value = getDisplayValue();
    return (
      <Wrapper disabled={disableColumnHeaderFilter}>
        <SingleInputDateRangeField
          onClick={() => {
            setOpenCalendar(true);
          }}
          inputRef={refs.setReference}
          onChange={(range) => {
            setRange(range);
          }}
          disabled={disableColumnHeaderFilter}
          tabIndex={tabIndex}
          style={{
            width: "100%",
            height: "100%",
          }}
          clearable={false}
          value={value}
        />
        {draftRange != null && (
          <FloatingPortal id={FLOATING_PORTAL_ROOT_ID}>
            <FloatingOverlay
              onClick={() => {
                setOpenCalendar(false);
              }}
              css={floatingCalendarOverlayStyles(theme)}
            />
            <div
              ref={refs.setFloating}
              css={floatingCalendarContainerStyles(theme)}
              style={floatingStyles}
            >
              <Flex>
                <PickersShortcuts
                  isValid={() => true}
                  items={shortcutsItems}
                  isLandscape={false}
                  onChange={handleDatePickerChanger}
                />
                <DateRangeCalendar
                  onChange={handleDatePickerChanger}
                  value={value}
                />
              </Flex>
              <PickersActionBar
                actions={["clear", "cancel", "accept"]}
                onAccept={() => {
                  setOpenCalendar(false);
                  applyRange(draftRange);
                }}
                onClear={() => {
                  setOpenCalendar(false);
                  clearRange();
                }}
                onCancel={() => {
                  setOpenCalendar(false);
                }}
                onSetToday={() => {}}
              />
            </div>
          </FloatingPortal>
        )}
      </Wrapper>
    );
  },
);

const Wrapper = styled.div<{ disabled: boolean | undefined }>`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  // keep 1px padding above to not cut off input outline
  padding: 1px 12px 11px;

  ${({ disabled }) =>
    disabled && {
      opacity: 0.4,
      pointerEvents: "none",
    }}
`;

const floatingCalendarOverlayStyles = (theme: ExactTheme) => css`
  z-index: ${theme.zIndices.popovers};
`;
const floatingCalendarContainerStyles = (theme: ExactTheme) => css`
  background: white;
  border-radius: 6px;
  box-shadow: ${theme.shadowV2.tertiary};
  z-index: ${theme.zIndices.popovers + 1};
`;
const itemToRange = (item: { value?: any }) => {
  if (typeof item.value !== "string") {
    return [null, null] as Value;
  }
  const [startRaw, endRaw] = item.value ? item.value.split(",") : [null, null];

  const start = dayjs(startRaw);
  const end = dayjs(endRaw);

  return [start.isValid() ? start : null, end.isValid() ? end : null] as Value;
};
